import { Alert, Card, Segmented, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { HistoricalSimulationSelect } from './HistoricalSimulationSelect';
import { UploadHistoricalShipments } from './UploadHistoricalShipments';
import { CloudUploadOutlined, RetweetOutlined } from '@ant-design/icons';
import logo from '~/assets/symbol.png';
import { useStoreState } from '../../../store';
import { UsePreviousHistoricalShipments } from './UsePreviousHistoricalShipments';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { SIMULATION_ID_SEARCH_PARAM } from '~/pages/Simulation/SimulationLandingPage';
import { useSimulationVersion } from '~/hooks/simulation/useSimulationVersion';
import { useSession } from '~/hooks/useSession';

export const HISTORICAL_DATASET_MODE = 'historical';
export const UPLOAD_DATASET_MODE = 'upload_new';
export const PREVIOUS_UPLOAD_DATASET_MODE = 'upload_previous';
export const SHIPMENT_SELECTION_KEY = 'shipmentSelectionKey';

const { Title } = Typography;

export const SimulationShimpentSelect = () => {
  const simulationSetupData = useStoreState((state) => state.simulation.simulationSetupData);
  const setupMode = simulationSetupData?.[SHIPMENT_SELECTION_KEY]?.mode;
  const [query] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const { version: serviceVersion } = useSimulationVersion(simulationId);
  const { sessionPartnerUrl } = useSession();

  // v2 upload/export tabs are launchers (banner + open the page in a new tab); v1 is unchanged.
  const isV2 = serviceVersion === 'v2';

  // v2 defaults to the previously-uploaded picker (its real selection surface); v1 keeps upload_new.
  const [mode, setMode] = useState(isV2 ? PREVIOUS_UPLOAD_DATASET_MODE : UPLOAD_DATASET_MODE);

  useEffect(() => {
    if (setupMode === HISTORICAL_DATASET_MODE) {
      setMode(HISTORICAL_DATASET_MODE);
    }
  }, [setupMode]);

  const handleModeChange = (val: string) => {
    setMode(val);
    if (!isV2) return;
    if (val === UPLOAD_DATASET_MODE) {
      window.open(`/${sessionPartnerUrl}/simulation/upload`, '_blank', 'noreferrer');
    } else if (val === HISTORICAL_DATASET_MODE) {
      window.open(`/${sessionPartnerUrl}/carrier-selection/export`, '_blank', 'noreferrer');
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <div style={{ marginBottom: 10 }}>
          <Segmented
            value={mode}
            onChange={(val) => handleModeChange(val as string)}
            options={[
              {
                label: (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div>
                      <img src={logo} alt={'Shipium'} title="Shipium" style={{ width: '20px' }} />
                    </div>
                    <Title level={5}>Use a Historical Shipium Dataset</Title>
                  </div>
                ),
                value: HISTORICAL_DATASET_MODE,
              },
              {
                label: (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div>
                      <CloudUploadOutlined style={{ fontSize: '24px' }} />
                    </div>
                    <Title level={5}>Upload a Simulation Dataset</Title>
                  </div>
                ),
                value: UPLOAD_DATASET_MODE,
              },
              {
                label: (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div>
                      <RetweetOutlined style={{ fontSize: '24px' }} />
                    </div>
                    <Title level={5}>Use Previous Simulation Dataset</Title>
                  </div>
                ),
                value: PREVIOUS_UPLOAD_DATASET_MODE,
              },
            ]}
          />
        </div>
      </div>
      {HISTORICAL_DATASET_MODE === mode &&
        (isV2 ? (
          <Alert
            style={{ marginTop: 10 }}
            message="Historical shipments come from a data export"
            description={
              <>
                Create a simulation-format export on the{' '}
                <a href={`/${sessionPartnerUrl}/carrier-selection/export`} target="_blank" rel="noreferrer">
                  Exports page
                </a>
                , register it as an upload on the{' '}
                <a href={`/${sessionPartnerUrl}/simulation/upload`} target="_blank" rel="noreferrer">
                  Uploads page
                </a>
                , then select it here under &quot;Use Previous Simulation Dataset&quot;.
              </>
            }
            type="info"
            showIcon
          />
        ) : (
          <HistoricalSimulationSelect />
        ))}
      {UPLOAD_DATASET_MODE === mode && <UploadHistoricalShipments />}
      {PREVIOUS_UPLOAD_DATASET_MODE === mode && <UsePreviousHistoricalShipments />}
    </Card>
  );
};
